import React from "react";
import clsx from "clsx";

import Modal from "../../../atoms/Modal";
import {
  CREATE_GROUP_SUCCESS_MESSAGE,
  Context,
  clearSuccessMessage,
  createGroup,
  deleteGroup,
  fetchClasses,
  fetchGroups,
  updateGroup,
} from "../../../teacherStore";
import useWaitApiCall from "../../../hooks/useWaitApiCall";
import Button from "../../../atoms/Button";
import GroupClassList from "../GroupClassList";
import { IGroup } from "../../../state";
import ModalEditGroup from "../../../molecules/teacher/ModalEditGroup";
import Dropdown from "../../../atoms/Dropdown";

import styles from "./ModalEditGroupClass.module.scss";

export type Props = {
  classIDs: number[];
  onClose: () => void;
};

const ModalEditGroupClass: React.FC<Props> = ({ classIDs, onClose }) => {
  const { state, dispatch } = React.useContext(Context);
  const [_calling, peep] = useWaitApiCall(state);

  const [groupName, setGroupName] = React.useState("");
  const [openModalEditGroup, setOpenModalEditGroup] = React.useState<{
    group: IGroup | null;
    state: boolean;
  }>(() => ({
    group: null,
    state: false,
  }));
  const [selectedGroupDelete, setSelectedGroupDelete] = React.useState<
    number | null
  >(null);

  const [selectedClass, setSelectedClass] = React.useState<number>(0);

  const classIDsStr = classIDs.join(",");
  const clsIDs = React.useMemo(
    () => classIDsStr.split(",").map(s => Number(s)),
    [classIDsStr],
  );
  const classes = React.useMemo(
    () => state.classes.filter(cls => clsIDs.includes(cls.id)),
    [clsIDs, state.classes],
  );
  const options = React.useMemo(() => {
    return classes.map(klass => ({
      value: klass.id,
      label: klass.full_name,
    }));
  }, [classes]);

  React.useEffect(() => {
    !!options.length && setSelectedClass(options[0].value);
  }, [options]);

  React.useEffect(() => {
    if (state.success_message) {
      setOpenModalEditGroup({
        group: null,
        state: false,
      });
    }
  }, [state.success_message]);

  React.useEffect(() => {
    if (state.success_message === CREATE_GROUP_SUCCESS_MESSAGE) {
      dispatch(fetchGroups(clsIDs));
      dispatch(clearSuccessMessage());
    }
  }, [dispatch, state.success_message, clsIDs]);

  React.useEffect(() => {
    dispatch(fetchGroups(clsIDs));
    dispatch(fetchClasses());
  }, [clsIDs, dispatch]);

  const onGroupCreationSubmit = React.useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      selectedClass && dispatch(peep(createGroup(selectedClass, groupName)));
      setGroupName("");
    },
    [dispatch, peep, groupName, selectedClass],
  );

  const updateGroupFunc = React.useCallback(
    (group: IGroup) => {
      dispatch(peep(updateGroup(group)));
    },
    [dispatch, peep],
  );

  const deleteGroupFunc = React.useCallback(
    (groupId: number) => dispatch(peep(deleteGroup(groupId))),
    [dispatch, peep],
  );

  const updateGroupName = (group: IGroup) => {
    setOpenModalEditGroup({ group, state: true });
  };

  const deleteGroupName = (id: number) => {
    setSelectedGroupDelete(id);
  };

  return (
    <Modal className={styles.modal} onClose={onClose}>
      {openModalEditGroup.state && (
        <ModalEditGroup
          group={openModalEditGroup.group}
          onSave={updateGroupFunc}
          onClose={() => setOpenModalEditGroup({ state: false, group: null })}
        />
      )}
      {selectedGroupDelete && (
        <Modal
          onClose={() => setSelectedGroupDelete(null)}
          className={styles.modalNotify}
        >
          <div className={styles.modalNotify__title}>
            グループを本当に削除しますか？
          </div>
          <div className={styles.modalNotify__actions}>
            <button
              className={styles.btnCancel}
              onClick={() => setSelectedGroupDelete(null)}
            >
              キャンセル
            </button>
            <button
              className={styles.btnDelete}
              onClick={() => {
                deleteGroupFunc(selectedGroupDelete);
                setSelectedGroupDelete(null);
              }}
            >
              削除
            </button>
          </div>
        </Modal>
      )}
      <div className={styles.modal__content}>
        <h5 className={styles.title}>グループ</h5>
        <GroupClassList
          groups={state.groups}
          displayClassName={clsIDs.length > 1}
          onUpdateGroupName={updateGroupName}
          onDeleteGroupName={deleteGroupName}
        />
        <form
          className={clsx(
            "d-flex align-items-center justify-content-between",
            styles.groupForm,
          )}
          onSubmit={onGroupCreationSubmit}
        >
          {clsIDs.length > 1 && (
            <Dropdown
              options={options}
              defaultValue={options[0]}
              className={styles.classSelect}
              classNameOptions={styles.options}
              onChange={value => {
                setSelectedClass(value);
              }}
            />
          )}
          <input
            type="text"
            id="addName"
            placeholder="グループ名"
            value={groupName}
            onChange={e => setGroupName(e.target.value)}
            required
          />
          <Button name="グループを作成" variant="normal" />
        </form>
        <div className="d-flex justify-content-end">
          <Button
            className={clsx("px-2", styles.btnDone)}
            onClick={onClose}
            name="完了"
            variant="normal"
          />
        </div>
      </div>
    </Modal>
  );
};

export default ModalEditGroupClass;
